import { useOutletContext } from "react-router";

export type SourceConnection = {
  id: string;
  email: string;
  displayName: string;
  status: string;
  updatedAt?: number;
  provider?: string;
  siteUrl?: string | null;
  username?: string | null;
  webhookUrl?: string | null;
  webhookSecret?: string | null;
  metaJson?: string | null;
};

export type AvailableSource = {
  provider: string;
  label: string;
  description: string;
};

/** Sources AttendeeSync can connect (platform-hosted OAuth). */
export const AVAILABLE_SOURCES: AvailableSource[] = [
  {
    provider: "zoom",
    label: "Zoom",
    description:
      "Authorize your Zoom account to read meetings and webinars and keep conference data synchronized. No Marketplace app setup is required.",
  },
  {
    provider: "google_meet",
    label: "Google Meet",
    description:
      "Authorize Google Calendar to sync attendees from calendar appointments on a schedule, including Meet-linked events. YouTube Live viewer lists are not available via Google APIs.",
  },
  {
    provider: "google_forms",
    label: "Google Forms",
    description:
      "Authorize Google to sync responses from forms that collect email addresses. Paste an edit URL or file ID. Scheduled sync only.",
  },
  {
    provider: "linkedin_events",
    label: "LinkedIn Events",
    description:
      "Authorize LinkedIn to sync Lead Gen form registrants from Company Page events. Live stream viewer lists are not available via LinkedIn APIs.",
  },
  {
    provider: "heysummit",
    label: "HeySummit",
    description:
      "Paste your HeySummit API token to sync summit attendees to your CRM. Webhooks are registered automatically for near-real-time updates.",
  },
  {
    provider: "fluent_forms",
    label: "Fluent Forms",
    description:
      "Connect a WordPress site with Fluent Forms using an Application Password. Pull form submissions on a schedule and configure Pro webhooks for live updates.",
  },
  {
    provider: "constant_contact",
    label: "Constant Contact",
    description:
      "Authorize Constant Contact to sync registrants from Events on a schedule. Contact lists and email campaigns are not used as sources.",
  },
  {
    provider: "mailchimp",
    label: "Mailchimp",
    description:
      "Connect with OAuth or paste an API key to sync subscribed audience members into your CRM on a schedule.",
  },
  {
    provider: "eventbrite",
    label: "Eventbrite",
    description:
      "Connect with OAuth or paste a private token to sync Eventbrite attendees into your CRM on a schedule.",
  },
  {
    provider: "microsoft_teams",
    label: "Microsoft Teams",
    description:
      "Authorize Microsoft to sync registrants from Teams webinars you organize or co-organize, plus attendees from Outlook calendar appointments. Requires VirtualEvent.Read and Calendars.Read.",
  },
];

export type DestinationField = {
  key: string;
  label: string;
  secret: boolean;
  help?: string | null;
  required: boolean;
};

export type CredentialHint = {
  key: string;
  label: string;
  masked: string;
};

export type DestinationProvider = {
  provider: string;
  label: string;
  kind: "destination";
  description: string;
  recommendation?: string | null;
  docsUrl?: string | null;
  docsLabel?: string | null;
  fields: DestinationField[];
  configured: boolean;
  credentialId: string | null;
  hintMask: string | null;
  credentialHints?: CredentialHint[];
  connectedAt?: number | null;
  status?: "ga" | "beta" | "disabled";
};

export type IntegrationsOutletContext = {
  orgId: string;
  canManage: boolean;
  organizationName: string | null | undefined;
  sourceConnections: Record<string, SourceConnection[]>;
  destinations: DestinationProvider[];
  message: string | null;
  setMessage: (message: string | null) => void;
  refresh: () => Promise<void>;
};

export function useIntegrationsOutlet() {
  return useOutletContext<IntegrationsOutletContext>();
}

export function formatConnectedOn(seconds: number | null | undefined): string {
  if (seconds == null) return "Unknown";
  return new Date(seconds * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function activeConnection(
  sourceConnections: Record<string, SourceConnection[]>,
  provider: string,
): SourceConnection | null {
  const list = sourceConnections[provider] ?? [];
  return list.find((z) => z.status === "active") ?? list[0] ?? null;
}
